import React, { useState, useEffect, useRef, useCallback } from "react";
import "./Chat.css";

const Chat = ({ chatId, chatName, authToken, userId, userRole }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chatMembers, setChatMembers] = useState([]);
  const messagesEndRef = useRef(null);

  // API URL base
  const API_URL = "https://ajje4ng1me.execute-api.us-east-1.amazonaws.com/prod";

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Fetch messages from API
  const fetchMessages = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: authToken ? `Bearer ${authToken}` : undefined,
      };

      const response = await fetch(`${API_URL}/chats/${chatId}/messages`, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        throw new Error(`Error fetching messages: ${response.status}`);
      }

      const data = await response.json();
      setMessages(data.messages || []);
    } catch (err) {
      setError("Failed to load messages. Please try again later.");
      console.error("Error fetching messages:", err);
    } finally {
      setLoading(false);
    }
  }, [API_URL, authToken, chatId]);

  // Fetch chat members
  const fetchChatMembers = useCallback(async () => {
    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: authToken ? `Bearer ${authToken}` : undefined,
      };

      const response = await fetch(`${API_URL}/chats/${chatId}/members`, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        throw new Error(`Error fetching members: ${response.status}`);
      }

      const data = await response.json();
      setChatMembers(data.members || []);
    } catch (err) {
      console.error("Error fetching chat members:", err);
    }
  }, [API_URL, authToken, chatId]);

  // Fetch messages on component mount
  useEffect(() => {
    if (chatId) {
      fetchMessages();
      fetchChatMembers();
    }
  }, [chatId, fetchMessages, fetchChatMembers]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Send message
  const sendMessage = async (e) => {
    e.preventDefault();

    if (!newMessage.trim()) return;

    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: authToken ? `Bearer ${authToken}` : undefined,
      };

      const response = await fetch(`${API_URL}/chats/${chatId}/messages`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          text: newMessage,
          userId: userId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Error sending message: ${response.status}`);
      }

      // Optimistically add the message to the UI
      const timestamp = new Date().toISOString();
      const newMsg = {
        id: `temp-${Date.now()}`,
        text: newMessage,
        sender: {
          id: userId,
          name: "You", // This will be replaced when we fetch messages again
        },
        timestamp,
      };

      setMessages([...messages, newMsg]);
      setNewMessage("");

      // Refresh messages to get the actual message with server ID
      fetchMessages();
    } catch (err) {
      setError("Failed to send message. Please try again.");
      console.error("Error sending message:", err);
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // Format date
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString();
  };

  // Group messages by date
  const groupMessagesByDate = () => {
    const groups = {};
    messages.forEach((message) => {
      const date = formatDate(message.timestamp);
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(message);
    });
    return groups;
  };

  // Check if user is admin
  const isAdmin = userRole === "admin";

  // Delete message (admin only)
  const deleteMessage = async (messageId) => {
    if (!isAdmin) return;

    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: authToken ? `Bearer ${authToken}` : undefined,
      };

      const response = await fetch(
        `${API_URL}/chats/${chatId}/messages/${messageId}`,
        {
          method: "DELETE",
          headers,
        }
      );

      if (!response.ok) {
        throw new Error(`Error deleting message: ${response.status}`);
      }

      // Remove the message from the UI
      setMessages(messages.filter((msg) => msg.id !== messageId));
    } catch (err) {
      setError("Failed to delete message. Please try again.");
      console.error("Error deleting message:", err);
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
  };

  // Placeholder for when no chat is selected
  if (!chatId) {
    return (
      <div className="chat-container no-chat-selected">
        <div className="no-chat-message">
          <h3>No chat selected</h3>
          <p>Please select a chat to start messaging</p>
        </div>
      </div>
    );
  }

  const messageGroups = groupMessagesByDate();

  return (
    <div className="chat-container">
      <div className="chat-header">
        <h2>{chatName || `Chat #${chatId}`}</h2>
        <div className="chat-members-count">
          {chatMembers.length} member{chatMembers.length !== 1 ? "s" : ""}
        </div>
      </div>

      {error && <div className="chat-error">{error}</div>}

      <div className="chat-messages">
        {loading && messages.length === 0 ? (
          <div className="chat-loading">Loading messages...</div>
        ) : messages.length === 0 ? (
          <div className="no-messages">
            No messages yet. Be the first to say hello!
          </div>
        ) : (
          Object.entries(messageGroups).map(([date, msgs]) => (
            <div key={date} className="message-date-group">
              <div className="date-divider">
                <span>{date}</span>
              </div>
              {msgs.map((message) => (
                <div
                  key={message.id}
                  className={`message ${
                    message.sender.id === userId ? "own-message" : ""
                  }`}
                >
                  <div className="message-header">
                    <span className="message-sender">
                      {message.sender.name}
                    </span>
                    <span className="message-time">
                      {formatTime(message.timestamp)}
                    </span>
                  </div>
                  <div className="message-body">{message.text}</div>
                  {isAdmin && (
                    <button
                      className="delete-message-btn"
                      onClick={() => deleteMessage(message.id)}
                      title="Delete message"
                    >
                      &times;
                    </button>
                  )}
                </div>
              ))}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <form className="chat-input-form" onSubmit={sendMessage}>
        <input
          type="text"
          value={newMessage}
          onChange={handleInputChange}
          placeholder="Type a message..."
          className="chat-input"
        />
        <button
          type="submit"
          className="send-button"
          disabled={!newMessage.trim()}
        >
          Send
        </button>
      </form>
    </div>
  );
};

export default Chat;
